import React, { useCallback, useMemo, useState } from 'react';
import { BulkImportModal } from '../uis/SlideLevelClassificationUI/BulkImportModal';
import bulkModalStyles from '../uis/SlideLevelClassificationUI/BulkImportModal.module.css';
import { ItemMetadataEditor } from '../itemMetadata/ItemMetadataEditor';
import {
    getRawItemMetadataSection,
    mergeItemMetadataColumnsWithRowKeys,
    normalizeItemMetadataColumnEntry,
    normalizeItemMetadataConfig,
} from '../itemMetadata/itemMetadataModel';
import { computeItemMetadataCoverage, normalizeFilename } from '../itemMetadata/itemInfoUtils';
import formStyles from '../uis/SlideLevelClassificationUI/ConfigForm.module.css';
import styles from '../ProjectEditForm.module.css';

const EMPTY_ROWS = [];

function ItemMetadataSettingsPanel({
    annotationUI,
    setAnnotationUI,
    projectItems = [],
    folderId,
    onReloadProjectItems,
    settingsLocked = false,
}) {
    const config = annotationUI?.config ?? {};
    const im = getRawItemMetadataSection(config);
    const trimWhitespace = im.trimWhitespace !== false;
    const caseInsensitive = Boolean(im.caseInsensitive);
    const slideRows = Array.isArray(im.rows) ? im.rows : EMPTY_ROWS;

    const normalizedMeta = useMemo(
        () => normalizeItemMetadataConfig(config),
        [config],
    );

    const effectiveColumns = useMemo(() => {
        const rawCols = getRawItemMetadataSection(config);
        const cols = rawCols.itemMetadataColumns ?? rawCols.slideMetadataColumns;
        if (Array.isArray(cols) && cols.length > 0) {
            return cols.map((c) => normalizeItemMetadataColumnEntry(c)).filter(Boolean);
        }
        return normalizedMeta.itemMetadata.itemMetadataColumns;
    }, [config, normalizedMeta.itemMetadata.itemMetadataColumns]);

    const [bulkOpen, setBulkOpen] = useState(false);
    const [draftColId, setDraftColId] = useState('');
    const [draftColError, setDraftColError] = useState('');

    const matchOpts = useMemo(
        () => ({ trimWhitespace, caseInsensitive }),
        [trimWhitespace, caseInsensitive],
    );

    const coverage = useMemo(
        () => computeItemMetadataCoverage(projectItems, slideRows, matchOpts),
        [projectItems, slideRows, matchOpts],
    );

    const persistSlice = useCallback(
        (nextRows, nextColumns, trim = trimWhitespace, ci = caseInsensitive) => {
            const cols = mergeItemMetadataColumnsWithRowKeys(
                nextColumns,
                nextRows,
                config.columnLabels || {},
            );
            const labels = { ...(config.columnLabels || {}) };
            cols.forEach((c) => {
                const raw = String(c.label ?? '');
                if (raw === '') {
                    delete labels[c.id];
                } else {
                    labels[c.id] = raw;
                }
            });
            const displayKeys = cols.filter((c) => c.showInViewer).map((c) => c.id);
            setAnnotationUI((prev) => {
                const prevCfg = prev.config ?? {};
                const { slideInfo: _si, slideInfoDisplayKeys: _sdk, ...restCfg } = prevCfg;
                return {
                    ...prev,
                    config: {
                        ...restCfg,
                        itemMetadata: {
                            rows: nextRows,
                            itemMetadataColumns: cols,
                            trimWhitespace: trim,
                            caseInsensitive: ci,
                        },
                        columnLabels: labels,
                        itemMetadataDisplayKeys: displayKeys,
                    },
                };
            });
        },
        [config.columnLabels, setAnnotationUI, trimWhitespace, caseInsensitive],
    );

    function handleBulk(parsed, mergeMode) {
        let nextRows;
        if (mergeMode === 'replace') {
            nextRows = parsed;
        } else {
            const map = new Map();
            const mergeRow = (r) => {
                const k = normalizeFilename(r.filename, matchOpts);
                if (!k) return;
                const prev = map.get(k) || {};
                map.set(k, { ...prev, ...r, filename: r.filename });
            };
            slideRows.forEach((r) => mergeRow(r));
            parsed.forEach((r) => mergeRow(r));
            nextRows = [...map.values()];
        }
        persistSlice(nextRows, effectiveColumns);
    }

    function downloadTemplate() {
        const blob = new Blob(
            ['filename,stain,batch\nexample.svs,H&E,1\n'],
            { type: 'text/csv;charset=utf-8' },
        );
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'item-metadata-template.csv';
        a.click();
        URL.revokeObjectURL(url);
    }

    function updateMetadataColumn(i, patch) {
        const next = effectiveColumns.map((c, j) => (j === i ? { ...c, ...patch } : c));
        persistSlice(slideRows, next);
    }

    function removeMetadataColumn(i) {
        const id = effectiveColumns[i]?.id;
        if (!id) return;
        const nextCols = effectiveColumns.filter((_, j) => j !== i);
        const nextRows = slideRows.map((r) => {
            const o = { ...r };
            delete o[id];
            return o;
        });
        persistSlice(nextRows, nextCols);
    }

    function addMetadataColumn() {
        const id = draftColId.trim();
        if (!id) {
            setDraftColError('Column key is required.');
            return;
        }
        if (id === 'filename') {
            setDraftColError('"filename" is reserved (used internally for matching).');
            return;
        }
        if (effectiveColumns.some((c) => c.id === id)) {
            setDraftColError(`Column "${id}" already exists.`);
            return;
        }
        setDraftColError('');
        persistSlice(slideRows, [
            ...effectiveColumns,
            { id, label: '', showInViewer: true },
        ]);
        setDraftColId('');
    }

    const itemCount = projectItems.length;
    const orphanCount = coverage.orphanFilenames.length;
    const unmatchedCount = coverage.unmatchedItemNames.length;

    return (
        <section
            id="project-section-item-metadata"
            className={styles.settingsScrollAnchor}
            aria-label="Per-item metadata"
        >
            <div className={styles.panel}>
                <h4 className={styles.panelTitle}>Per-item metadata (project)</h4>
                {settingsLocked && (
                    <p className={styles.panelLockBanner}>
                        Published: return to draft to edit this section.
                    </p>
                )}
                <div className={settingsLocked ? styles.panelDisabled : undefined}>
                    <p className={formStyles.intro}>
                        Optional CSV-style metadata matched to each slide by Girder item
                        {' '}
                        <strong>name</strong>
                        . Shown in the annotation viewer for all annotation UI types on this project.
                    </p>

                    <div className={formStyles.checkboxRow}>
                        <label>
                            <input
                                type="checkbox"
                                checked={trimWhitespace}
                                disabled={settingsLocked}
                                onChange={(e) => persistSlice(
                                    slideRows,
                                    effectiveColumns,
                                    e.target.checked,
                                    caseInsensitive,
                                )}
                            />
                            Trim whitespace when matching
                        </label>
                        <label>
                            <input
                                type="checkbox"
                                checked={caseInsensitive}
                                disabled={settingsLocked}
                                onChange={(e) => persistSlice(
                                    slideRows,
                                    effectiveColumns,
                                    trimWhitespace,
                                    e.target.checked,
                                )}
                            />
                            Case-insensitive filename match
                        </label>
                    </div>

                    <p className={formStyles.intro}>
                        Define metadata columns (keys), then edit one cell per slide or use import.
                        {' '}
                        <strong>Show in viewer</strong>
                        {' '}
                        controls which columns annotators see; hidden values are omitted from the API
                        for non-admin readers.
                    </p>

                    {effectiveColumns.length === 0 ? (
                        <p className={formStyles.intro}>No metadata columns yet. Add a column key to begin.</p>
                    ) : (
                        <table className={formStyles.table}>
                            <thead>
                                <tr>
                                    <th>Column key</th>
                                    <th>Label in viewer</th>
                                    <th>Show in viewer</th>
                                    <th aria-label="Remove" />
                                </tr>
                            </thead>
                            <tbody>
                                {effectiveColumns.map((col, i) => (
                                    <tr key={col.id}>
                                        <td><code>{col.id}</code></td>
                                        <td>
                                            <input
                                                className={formStyles.input}
                                                value={col.label ?? ''}
                                                disabled={settingsLocked}
                                                onChange={(e) => updateMetadataColumn(i, { label: e.target.value })}
                                                aria-label={`Label for column ${col.id}`}
                                            />
                                        </td>
                                        <td>
                                            <label>
                                                <input
                                                    type="checkbox"
                                                    checked={col.showInViewer !== false}
                                                    disabled={settingsLocked}
                                                    onChange={(e) => updateMetadataColumn(i, {
                                                        showInViewer: e.target.checked,
                                                    })}
                                                />
                                            </label>
                                        </td>
                                        <td>
                                            <button
                                                type="button"
                                                className={`${formStyles.button} ${formStyles.buttonDanger}`}
                                                disabled={settingsLocked}
                                                onClick={() => removeMetadataColumn(i)}
                                            >
                                                Remove
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}

                    <div className={formStyles.inlineRow}>
                        <input
                            className={[
                                formStyles.input,
                                draftColError ? formStyles.inputError : '',
                            ].filter(Boolean).join(' ')}
                            placeholder="New column key (e.g. stain)"
                            value={draftColId}
                            disabled={settingsLocked}
                            aria-invalid={Boolean(draftColError)}
                            aria-describedby={draftColError ? 'item-meta-new-col-error' : undefined}
                            onChange={(e) => {
                                setDraftColId(e.target.value);
                                setDraftColError('');
                            }}
                            aria-label="New metadata column key"
                        />
                        <button
                            type="button"
                            className={formStyles.button}
                            disabled={settingsLocked || !String(draftColId ?? '').trim()}
                            onClick={addMetadataColumn}
                        >
                            Add column
                        </button>
                    </div>
                    {draftColError ? (
                        <div className={formStyles.inlineErrorRow}>
                            <span id="item-meta-new-col-error" className={formStyles.inlineErrorText} role="alert">
                                {draftColError}
                            </span>
                        </div>
                    ) : null}

                    <div className={formStyles.btnRow}>
                        <button
                            type="button"
                            className={formStyles.button}
                            disabled={settingsLocked}
                            onClick={() => setBulkOpen(true)}
                        >
                            Import from CSV or paste…
                        </button>
                    </div>

                    <ItemMetadataEditor
                        projectItems={projectItems}
                        rows={slideRows}
                        metadataColumns={effectiveColumns}
                        matchOpts={matchOpts}
                        onRowsChange={(next) => persistSlice(next, effectiveColumns)}
                    />

                    <p className={formStyles.intro}>
                        {slideRows.length}
                        {' '}
                        metadata row
                        {slideRows.length === 1 ? '' : 's'}
                        {' '}
                        loaded.
                    </p>

                    {itemCount === 0 ? (
                        <div className={formStyles.coverage}>
                            <p className={formStyles.coverageMuted}>
                                Add slides in Items (or reload) to validate filename coverage.
                                {folderId && onReloadProjectItems && (
                                    <>
                                        {' '}
                                        <button
                                            type="button"
                                            className={`${formStyles.button} ${formStyles.buttonMuted}`}
                                            onClick={onReloadProjectItems}
                                        >
                                            Reload items
                                        </button>
                                    </>
                                )}
                            </p>
                        </div>
                    ) : (
                        <div className={formStyles.coverage}>
                            <p>
                                <strong>Coverage:</strong>
                                {' '}
                                {itemCount}
                                {' '}
                                slide
                                {itemCount === 1 ? '' : 's'}
                                {' '}
                                in project;
                                {' '}
                                {coverage.matchedCount}
                                {' '}
                                matched to metadata rows;
                                {' '}
                                {unmatchedCount}
                                {' '}
                                unmatched slide
                                {unmatchedCount === 1 ? '' : 's'}
                                ;
                                {' '}
                                {orphanCount}
                                {' '}
                                unused CSV row
                                {orphanCount === 1 ? '' : 's'}
                                .
                            </p>
                            {coverage.duplicateItemNames.length > 0 && (
                                <div className={formStyles.warn}>
                                    Duplicate item names (ambiguous metadata match):{' '}
                                    {coverage.duplicateItemNames.join(', ')}
                                </div>
                            )}
                            {(unmatchedCount > 0 || orphanCount > 0) && (
                                <details className={formStyles.coverageMuted}>
                                    <summary>Details</summary>
                                    {unmatchedCount > 0 && (
                                        <>
                                            <div>Unmatched slides</div>
                                            <ul className={formStyles.coverageList}>
                                                {coverage.unmatchedItemNames.map((n) => (
                                                    <li key={n}>{n}</li>
                                                ))}
                                            </ul>
                                        </>
                                    )}
                                    {orphanCount > 0 && (
                                        <>
                                            <div>Rows not matching any project item</div>
                                            <ul className={formStyles.coverageList}>
                                                {coverage.orphanFilenames.map((n) => (
                                                    <li key={n}>{n}</li>
                                                ))}
                                            </ul>
                                        </>
                                    )}
                                </details>
                            )}
                        </div>
                    )}

                    {bulkOpen && (
                        <BulkImportModal
                            mode="slideInfo"
                            onClose={() => setBulkOpen(false)}
                            onApply={(parsed, mergeMode) => {
                                handleBulk(parsed, mergeMode);
                                setBulkOpen(false);
                            }}
                            extraFooterActions={(
                                <button
                                    type="button"
                                    className={bulkModalStyles.btnMuted}
                                    onClick={downloadTemplate}
                                >
                                    Download CSV template
                                </button>
                            )}
                        />
                    )}
                </div>
            </div>
        </section>
    );
}

export default React.memo(ItemMetadataSettingsPanel);
